import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getSavedExtracts,
  KeywordByAsinRow,
  estimateSalesToFirstPage,
} from "../../services/jungleScout";
import CardComponent from "../shared/CardComponent";
import TableComponent, { Column } from "../shared/TableComponent";

type SavedExtract = {
  id: string;
  asin: string;
  marketplace: string;
  extractedAt: string;
  keywords: KeywordByAsinRow[];
};

export default function KeywordsSaved() {
  const { t } = useTranslation();
  const module = "modules.amazonExtract.";
  const keywordsModule = "modules.keywordsSaved.";
  const [loading, setLoading] = useState(true);
  const [extracts, setExtracts] = useState<SavedExtract[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const loadExtracts = useCallback(async () => {
    setLoading(true);
    try {
      const list = await getSavedExtracts();
      setExtracts(list);
    } catch {
      setExtracts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadExtracts();
  }, [loadExtracts]);

  const formatDate = (iso: string) => {
    try {
      const d = new Date(iso);
      return isNaN(d.getTime()) ? iso : d.toLocaleString();
    } catch {
      return iso;
    }
  };

  const formatNumber = (v: number | null | undefined) =>
    v != null ? String(v) : "—";
  const formatCurrency = (v: number | null | undefined) =>
    v != null ? `$${Number(v).toFixed(2)}` : "—";

  const getKeywordColumns = (): Column[] => [
    {
      header: t(module + "keyword"),
      accessor: "attributes.name",
      cell: (_, row) => (
        <Typography variant="body2">
          {row?.attributes?.name ?? "—"}
        </Typography>
      ),
    },
    {
      header: t(module + "organicRank"),
      accessor: "attributes.organic_rank",
      cell: (_, row) => (
        <Typography variant="body2">
          {formatNumber(row?.attributes?.organic_rank)}
        </Typography>
      ),
    },
    {
      header: t(module + "sponsoredRank"),
      accessor: "attributes.sponsored_rank",
      cell: (_, row) => (
        <Typography variant="body2">
          {formatNumber(row?.attributes?.sponsored_rank)}
        </Typography>
      ),
    },
    {
      header: t(module + "overallRank"),
      accessor: "attributes.overall_rank",
      cell: (_, row) => (
        <Typography variant="body2">
          {formatNumber(row?.attributes?.overall_rank)}
        </Typography>
      ),
    },
    {
      header: t(module + "salesToFirstPage"),
      accessor: "salesToFirstPage",
      cell: (data) => (
        <Typography variant="body2">{formatNumber(data)}</Typography>
      ),
    },
    {
      header: t(module + "ppcBidFirstPage"),
      accessor: "attributes.ppc_bid_exact",
      cell: (_, row) => (
        <Typography variant="body2">
          {formatCurrency(row?.attributes?.ppc_bid_exact)}
        </Typography>
      ),
    },
    {
      header: t(module + "ppcBidBroad"),
      accessor: "attributes.ppc_bid_broad",
      cell: (_, row) => (
        <Typography variant="body2">
          {formatCurrency(row?.attributes?.ppc_bid_broad)}
        </Typography>
      ),
    },
    {
      header: t(module + "searchVolumeExact"),
      accessor: "attributes.monthly_search_volume_exact",
      cell: (_, row) => (
        <Typography variant="body2">
          {formatNumber(row?.attributes?.monthly_search_volume_exact)}
        </Typography>
      ),
    },
  ];

  const keywordColumns = getKeywordColumns();

  const tableDataForExtract = (keywords: KeywordByAsinRow[]) =>
    keywords.map((k) => ({
      ...k,
      "attributes.name": k.attributes?.name,
      "attributes.organic_rank": k.attributes?.organic_rank,
      "attributes.sponsored_rank": k.attributes?.sponsored_rank,
      "attributes.overall_rank": k.attributes?.overall_rank,
      salesToFirstPage: estimateSalesToFirstPage(k.attributes),
      "attributes.ppc_bid_exact": k.attributes?.ppc_bid_exact,
      "attributes.ppc_bid_broad": k.attributes?.ppc_bid_broad,
      "attributes.monthly_search_volume_exact":
        k.attributes?.monthly_search_volume_exact,
    }));

  return (
    <CardComponent title={t(keywordsModule + "title")} isLoading={loading}>
      <Box>
        {extracts.length === 0 && !loading && (
          <Typography color="textSecondary">
            {t(keywordsModule + "noSaved")}
          </Typography>
        )}
        {extracts.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>{t(keywordsModule + "asin")}</TableCell>
                <TableCell>{t(keywordsModule + "marketplace")}</TableCell>
                <TableCell>{t(keywordsModule + "extractedAt")}</TableCell>
                <TableCell align="right">{t(keywordsModule + "keywordCount")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {extracts.map((extract) => {
                const isExpanded = expandedId === extract.id;
                const keywords = extract.keywords || [];
                const tableData = tableDataForExtract(keywords);
                return (
                  <React.Fragment key={extract.id}>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <IconButton
                          size="small"
                          onClick={() =>
                            setExpandedId(isExpanded ? null : extract.id)
                          }
                        >
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{extract.asin}</TableCell>
                      <TableCell>{extract.marketplace.toUpperCase()}</TableCell>
                      <TableCell>{formatDate(extract.extractedAt)}</TableCell>
                      <TableCell align="right">{keywords.length}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box margin={2}>
                            <TableComponent
                              data={tableData}
                              columns={keywordColumns}
                              perPage={10}
                              searchKeys={["attributes.name"]}
                            />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Box>
    </CardComponent>
  );
}
